import { useQuery } from '@tanstack/react-query';
import InputService from 'services/InputService';
import { INPUTS_QUERY_KEY } from 'constants/queryKeys';
import { QUERY_STALE_TIME_MS } from 'constants/appConstants';
import useFetchUserProfile from 'hooks/useFetchUserProfile';
import useFetchOrganisations from 'hooks/useFetchOrganisations';
import { InputType } from 'interfaces/IInput';

/**
 * Custom hook to fetch inputs by organization ID
 *
 * Returns plans on excluded farms and paddocks too. Views that display plan
 * data must read `useVisibleOrgData` instead, which hides them.
 * @param orgId - The organization ID to fetch inputs for
 * @param inputType - Optional filter for input type (InputType.Reserved, InputType.PlanActual, InputType.Recommendation)
 * @returns Query object containing inputs data, loading state, and error
 */
export const useFetchInputsByOrgId = (
  orgId: string | null,
  inputType?: InputType
) => {
  const { data: userData, isError: isUserDataError } = useFetchUserProfile();
  const { data: organisationsData, isError: isOrganisationsError } =
    useFetchOrganisations();

  return useQuery({
    queryKey: [INPUTS_QUERY_KEY, orgId],
    queryFn: async () => {
      if (!orgId) return [];
      return await InputService.GetByOrgActive(orgId, inputType);
    },
    enabled:
      !!orgId &&
      !!userData &&
      !isUserDataError &&
      !isOrganisationsError &&
      !!organisationsData,
    staleTime: QUERY_STALE_TIME_MS,
  });
};
